import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useGetApiCompaniesCurrentServicemodulesTravelexpensesServiceModuleIdEntriesCompanyPaginated } from '@/api/generated/company-management-user/company-management-user'
import type { OrderType, TravelExpenseEntryModelPaginatedResponse } from '@/api/model'
import { unwrap } from '@/api/unwrap'

const DEFAULT_PAGE_SIZE = 10

export interface TravelExpenseEntriesFilters {
  employeeUserId?: string
  /** ISO date or timestamp, only the local date is used */
  from?: string
  to?: string
}

/** The date picker sends local start/end of day as UTC ISO, the endpoints want local dates */
export function toLocalDate(value?: string): string | undefined {
  return value ? dayjs(value).format('YYYY-MM-DD') : undefined
}

/**
 * Data layer of the travel expense entry list: server-side pagination + sorting, filters come
 * from outside. Read-only, managers do not edit entries. Mirrors `useTimeTrackingEntries`.
 */
export function useTravelExpenseEntries(serviceModuleId: number, filters: TravelExpenseEntriesFilters) {
  const { employeeUserId, from, to } = filters

  const [sort, setSort] = useState<string>()
  const [order, setOrder] = useState<OrderType>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  // Filter change → back to the first page.
  useEffect(() => {
    setOffset(0)
  }, [employeeUserId, from, to])

  const listQuery = useGetApiCompaniesCurrentServicemodulesTravelexpensesServiceModuleIdEntriesCompanyPaginated(
    serviceModuleId,
    {
      Offset: offset,
      Limit: limit,
      Sort: sort,
      Order: order,
      employeeUserId,
      startDate: toLocalDate(from),
      endDate: toLocalDate(to),
    },
  )
  const list = unwrap<TravelExpenseEntryModelPaginatedResponse>(listQuery.data)

  return {
    rows: list?.items,
    totalCount: list?.totalCount,
    isLoading: listQuery.isLoading || listQuery.isFetching,
    isError: listQuery.isError,
    sort,
    order,
    onSort: (newSort?: string, newOrder?: OrderType) => {
      setSort(newSort)
      setOrder(newOrder)
      setOffset(0)
    },
    offset,
    limit,
    onOffsetChange: setOffset,
    onPageSizeChange: (pageSize: number) => {
      setLimit(pageSize)
      setOffset(0)
    },
  }
}

export type TravelExpenseEntriesController = ReturnType<typeof useTravelExpenseEntries>
